import { Link } from "react-router-dom";
import { ArrowRight, Network } from "lucide-react";
import type { ContentHubSection, HubLink } from "@/data/content/contentHubs";
import { ROUTE_PATHS } from "@/routes/routePaths";

type Props = {
  eyebrow: string;
  title: string;
  overview: string;
  sections: ContentHubSection[];
  relatedHubs?: HubLink[];
};

export function ContentHubLayout({ eyebrow, title, overview, sections, relatedHubs }: Props) {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border bg-muted/30">
        <div className="container py-10 sm:py-14">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">{eyebrow}</p>
          <h1 className="mt-2 max-w-3xl text-3xl font-bold text-foreground md:text-4xl">{title}</h1>
          <p className="mt-4 max-w-3xl text-base leading-relaxed text-muted-foreground">{overview}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link
              to={ROUTE_PATHS.FIND_RIDES}
              className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground"
            >
              Find rides <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to={ROUTE_PATHS.OFFER_A_RIDE}
              className="inline-flex items-center gap-2 rounded-lg border border-border px-5 py-2.5 text-sm font-semibold text-foreground hover:bg-muted/50"
            >
              Offer a ride
            </Link>
          </div>
        </div>
      </header>

      {sections.map((section) => (
        <section key={section.title} className="border-b border-border/60 py-10 sm:py-12 even:bg-muted/20">
          <div className="container">
            <div className="mx-auto max-w-5xl">
              <h2 className="text-xl font-bold text-foreground md:text-2xl">{section.title}</h2>
              {section.intro ? <p className="mt-2 text-sm text-muted-foreground">{section.intro}</p> : null}
              <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {section.links.map((link) => (
                  <HubLinkCard key={link.path} link={link} />
                ))}
              </div>
            </div>
          </div>
        </section>
      ))}

      {relatedHubs && relatedHubs.length > 0 ? (
        <section className="container py-10 sm:py-12">
          <div className="mx-auto max-w-5xl rounded-2xl border border-border bg-card p-6 sm:p-8">
            <div className="flex items-center gap-2">
              <Network className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-semibold text-foreground">Related hubs</h2>
            </div>
            <ul className="mt-4 space-y-2">
              {relatedHubs.map((hub) => (
                <li key={hub.path}>
                  <Link to={hub.path} className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline">
                    {hub.label} <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      ) : null}
    </div>
  );
}

function HubLinkCard({ link }: { link: HubLink }) {
  return (
    <Link
      to={link.path}
      className="group flex h-full flex-col rounded-xl border border-border bg-card p-4 transition-shadow hover:shadow-md"
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="font-semibold text-foreground group-hover:text-primary">{link.label}</h3>
        {link.distance ? <span className="shrink-0 text-xs text-muted-foreground">{link.distance}</span> : null}
      </div>
      {link.description ? <p className="mt-2 flex-1 text-sm text-muted-foreground">{link.description}</p> : null}
      <span className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary">
        View <ArrowRight className="h-3.5 w-3.5" />
      </span>
    </Link>
  );
}
